import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImageUrl = (url) => {
  try {
    if (!url) return url;
    const resolved = url.startsWith('/uploads') || url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url;
    if (!/\/api\/(files|employee)\//i.test(resolved)) return resolved;
    const token = localStorage.getItem('token');
    if (!token) return resolved;
    const separator = resolved.includes('?') ? '&' : '?';
    return `${resolved}${separator}access_token=${encodeURIComponent(token)}`;
  } catch {
    return url;
  }
};

export default function AdminVerifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsAuth, setNeedsAuth] = useState(false);

  const fetchPending = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/verification/pending`);
      setItems(res.data.items || []);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to load pending verifications';
      setError(detail);
      if (e.response?.status === 403) {
        setNeedsAuth(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const approve = async (item) => {
    try {
      await axios.post(`${API}/admin/verification/approve`, { email: item.email, note: 'Approved via admin UI' });
      setItems(prev => prev.filter(x => x.id !== item.id));
    } catch (e) {
      setError(e.response?.data?.detail || 'Approve failed');
    }
  };

  const reject = async (item) => {
    try {
      await axios.post(`${API}/admin/verification/reject`, { email: item.email, reason: 'Rejected via admin UI' });
      setItems(prev => prev.filter(x => x.id !== item.id));
    } catch (e) {
      setError(e.response?.data?.detail || 'Reject failed');
    }
  };


  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-5xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-yellow-400 mb-4">Pending ID Verifications</h1>
        <div className="mb-4 flex items-center gap-3">
          <button onClick={fetchPending} className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded">Refresh</button>
          {needsAuth && (
            <span className="text-red-400">Admin authorization required. Use the Admin link in the Home footer to login.</span>
          )}
        </div>
        {loading && <div>Loading...</div>}
        {error && !needsAuth && <div className="text-red-400">{error}</div>}
        {!loading && !needsAuth && items.length === 0 && <div className="text-gray-400">No pending verifications.</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(item => (
            <div key={item.id} className="border border-yellow-400 rounded p-4 bg-gray-900">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold">{item.name}</div>
                  <div className="text-sm text-gray-400">{item.email}</div>
                  <div className="text-xs text-gray-500">User ID: {item.id}</div>
                  {item.id_verification_id && (
                    <div className="text-xs text-gray-500">Verification ID: {item.id_verification_id}</div>
                  )}
                  <div className="text-xs text-gray-500">Role: {item.role || 'student'} • Tier: {item.subscription_tier || 'free'}</div>
                  <div className="text-xs text-gray-500">Last login: {item.last_login ? new Date(item.last_login).toLocaleString() : 'N/A'}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded bg-yellow-500 text-black">{item.id_verification_status}</span>
              </div>
              {item.id_photo_url && (
                <img src={resolveImageUrl(item.id_photo_url)} alt="ID" className="w-full h-48 object-cover rounded mb-3" />
              )}
              <div className="flex gap-2">
                <button onClick={() => approve(item)} className="bg-green-600 hover:bg-green-700 px-3 py-2 rounded">Approve</button>
                <button onClick={() => reject(item)} className="bg-red-600 hover:bg-red-700 px-3 py-2 rounded">Reject</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
